/**
 * Keyboard (GDD §8): Left and Right arrows or A and D steer; Up and Down arrows or W and S set the throttle; Space
 * fires while held (and starts a run from the title); P or Esc pauses. Keys are read by physical position (`code`),
 * so WASD sits under the same fingers on every layout. Every key releases when the window loses focus.
 */
import { createSource, releaseSource, type CommandSink, type ControlSource } from './actions.ts';

export type KeyRole = 'left' | 'right' | 'up' | 'down' | 'fire' | 'pause' | 'back' | 'confirm';

/** What a key does, by its `KeyboardEvent.code`. */
export function keyRole(code: string): KeyRole | null {
  switch (code) {
    case 'ArrowLeft':
    case 'KeyA':
      return 'left';
    case 'ArrowRight':
    case 'KeyD':
      return 'right';
    case 'ArrowUp':
    case 'KeyW':
      return 'up';
    case 'ArrowDown':
    case 'KeyS':
      return 'down';
    case 'Space':
      return 'fire';
    case 'KeyP':
      return 'pause';
    case 'Escape':
      return 'back';
    case 'Enter':
    case 'NumpadEnter':
      return 'confirm';
    default:
      return null;
  }
}

export interface Keyboard {
  readonly source: ControlSource;
  /** True while a menu has the keys: arrows move its focus, Space and Enter press its buttons natively. */
  menu: boolean;
  dispose(): void;
}

/**
 * Keyboard play and commands. `onPlayKey` reports that a game key was used, so the HUD can leave its touch layout
 * (judgment call 32).
 */
export function createKeyboard(target: Window, command: CommandSink, onPlayKey: () => void): Keyboard {
  const source = createSource();
  const held = { left: false, right: false, up: false, down: false };
  const kb: Keyboard = { source, menu: false, dispose };

  const apply = (): void => {
    source.steer = (held.right ? 1 : 0) - (held.left ? 1 : 0);
    source.throttle = (held.up ? 1 : 0) - (held.down ? 1 : 0);
  };

  function down(e: KeyboardEvent): void {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    const role = keyRole(e.code);
    if (!role) return;
    if (kb.menu) {
      // Menus: arrows up and down move the focus; left and right adjust a focused slider natively; Space and Enter
      // press the focused button natively.
      if (role === 'up' || role === 'down') {
        e.preventDefault();
        if (!e.repeat) command(role);
      } else if ((role === 'back' || role === 'pause') && !e.repeat) {
        e.preventDefault();
        command(role === 'back' ? 'back' : 'pause');
      }
      return;
    }
    e.preventDefault();
    onPlayKey();
    switch (role) {
      case 'left':
      case 'right':
      case 'up':
      case 'down':
        held[role] = true;
        apply();
        break;
      case 'fire':
        source.fire = true;
        if (!e.repeat) command('start');
        break;
      case 'confirm':
        if (!e.repeat) command('start');
        break;
      case 'pause':
      case 'back':
        if (!e.repeat) command('pause');
        break;
    }
  }

  function up(e: KeyboardEvent): void {
    const role = keyRole(e.code);
    if (!role) return;
    if (!kb.menu) e.preventDefault();
    if (role === 'left' || role === 'right' || role === 'up' || role === 'down') {
      held[role] = false;
      apply();
    } else if (role === 'fire') source.fire = false;
  }

  function blur(): void {
    held.left = held.right = held.up = held.down = false;
    releaseSource(source);
  }

  target.addEventListener('keydown', down);
  target.addEventListener('keyup', up);
  target.addEventListener('blur', blur);

  function dispose(): void {
    target.removeEventListener('keydown', down);
    target.removeEventListener('keyup', up);
    target.removeEventListener('blur', blur);
  }
  return kb;
}
